import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, UserMinus, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL } from '../config/api';

const Followers = ({ user, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('followers');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [followingIds, setFollowingIds] = useState(new Set());

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [followersRes, followingRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/${user.id}/followers`),
        axios.get(`${API_URL}/api/users/${user.id}/following`),
      ]);
      setFollowers(followersRes.data);
      setFollowing(followingRes.data);
      setFollowingIds(new Set(followingRes.data.map(u => u.id)));
    } catch (err) {
      console.error('Failed to fetch follow data:', err);
    }
    setLoading(false);
  }, [user]);

  const fetchAllUsers = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/api/users/search?query=&limit=50`);
      setAllUsers(res.data.filter(u => u.id !== user.id));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      fetchData();
      fetchAllUsers();
    }
  }, [isOpen, user, fetchData, fetchAllUsers]);

  const handleFollow = async (targetId) => {
    try {
      await axios.post(`${API_URL}/api/users/${targetId}/follow?follower_id=${user.id}`);
      setFollowingIds(prev => new Set([...prev, targetId]));
      fetchData();
    } catch (err) {
      console.error('Follow failed:', err);
    }
  };

  const handleUnfollow = async (targetId) => {
    try {
      await axios.delete(`${API_URL}/api/users/${targetId}/follow?follower_id=${user.id}`);
      setFollowingIds(prev => { const n = new Set(prev); n.delete(targetId); return n; });
      fetchData();
    } catch (err) {
      console.error('Unfollow failed:', err);
    }
  };

  const getInitials = (first, last) => {
    return `${(first || '?')[0]}${(last || '')[0] || ''}`.toUpperCase();
  };

  const filteredList = () => {
    const query = searchQuery.toLowerCase();
    if (activeTab === 'followers') {
      return followers.filter(u =>
        !query || (u.display_name || '').toLowerCase().includes(query) || (u.username || '').toLowerCase().includes(query)
      );
    }
    if (activeTab === 'following') {
      return following.filter(u =>
        !query || (u.display_name || '').toLowerCase().includes(query) || (u.username || '').toLowerCase().includes(query)
      );
    }
    // discover
    return allUsers.filter(u =>
      !followingIds.has(u.id) && (!query || (u.display_name || '').toLowerCase().includes(query) || (u.username || '').toLowerCase().includes(query))
    );
  };

  const renderUser = (u) => {
    const isFollowing = followingIds.has(u.id);
    return (
      <div key={u.id} className="flex items-center justify-between py-2.5 px-1 border-b border-zinc-800 last:border-0" data-testid={`follower-row-${u.id}`}>
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 overflow-hidden">
            {u.profile_picture
              ? <img src={u.profile_picture} alt="" className="w-full h-full object-cover" />
              : getInitials(u.first_name, u.last_name)}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{u.display_name || `${u.first_name} ${u.last_name}`}</p>
            {u.username && <p className="text-gray-500 text-xs truncate">@{u.username}</p>}
          </div>
        </div>
        {u.id !== user.id && (
          isFollowing ? (
            <Button
              onClick={() => handleUnfollow(u.id)}
              variant="outline"
              size="sm"
              className="text-xs h-7 px-3 border-zinc-600 text-gray-300 hover:bg-red-600/20 hover:text-red-400 hover:border-red-600 flex-shrink-0"
              data-testid={`unfollow-btn-${u.id}`}
            >
              <UserMinus className="h-3 w-3 mr-1" />Following
            </Button>
          ) : (
            <Button
              onClick={() => handleFollow(u.id)}
              size="sm"
              className="text-xs h-7 px-3 bg-red-600 hover:bg-red-700 text-white flex-shrink-0"
              data-testid={`follow-btn-${u.id}`}
            >
              <UserPlus className="h-3 w-3 mr-1" />Follow
            </Button>
          )
        )}
      </div>
    );
  };

  const list = filteredList();

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-zinc-900 text-white border-zinc-700 max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogTitle className="sr-only">Followers</DialogTitle>
        <DialogDescription className="sr-only">View and manage your followers and following</DialogDescription>

        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-zinc-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Users className="h-5 w-5 text-red-500" />Social
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
          </div>

          {/* Tabs */}
          <div className="flex bg-zinc-800 rounded-lg p-1 mb-3">
            {[
              { id: 'followers', label: `Followers (${followers.length})` },
              { id: 'following', label: `Following (${following.length})` },
              { id: 'discover', label: 'Find People' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeTab === tab.id ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`followers-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="bg-zinc-800 border-zinc-700 text-white text-sm h-9 pl-9"
              data-testid="followers-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-3 py-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-6 w-6 border-2 border-red-600 border-t-transparent rounded-full" />
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-10">
              <Users className="h-10 w-10 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">
                {activeTab === 'followers' ? 'No followers yet' :
                 activeTab === 'following' ? 'Not following anyone yet' :
                 'No users found'}
              </p>
            </div>
          ) : (
            list.map(renderUser)
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Followers;
